import { useEscapeLayer } from '@lib/escape'
import type { EditorValue } from '@octopus-do/octopus-editor'
import { artBoardActions } from '@store/reducers/artboard'
import cls from 'classnames'
import type React from 'react'
import { useDispatch } from 'react-redux'

import { NEW_THREAD_LAYER_ID } from '../../constants'
import {
  useNewThread,
  useNewThreadPosition,
  useThreadTarget,
} from '../../hooks'
import { getEventOffset } from '../../utils'
import { NewThreadForm } from '../NewThreadForm'
import css from './styles.module.css'

// The layer lives inside the gesture listener, so the canvas still pans and
// zooms under it; the id lets the drag handler treat it as the canvas.
export const NewThreadContainer: React.FC = () => {
  const dispatch = useDispatch()
  const { draft, position, onPointerDown, onClick, onReset } =
    useNewThreadPosition()

  const createThread = useNewThread()

  // the page under the cursor is highlighted until a click opens the form
  const { trackTarget, resetTarget } = useThreadTarget()

  const onPointerMove = (event: React.PointerEvent) => {
    if (draft) {
      return
    }

    trackTarget(getEventOffset(event))
  }

  const onLayerClick = (event: React.MouseEvent) => {
    resetTarget()
    onClick(event)
  }

  // The container is mounted only in the new_thread mode, so this layer is
  // the comment mode itself; the draft form above registers later and
  // therefore closes first.
  useEscapeLayer(true, () => dispatch(artBoardActions.changeMode('edit')))
  useEscapeLayer(draft !== null, onReset)

  const className = cls(css.container, { [css.active]: !!draft })

  const onSubmit = (next: EditorValue, author: string) => {
    if (!draft) {
      return
    }

    const text = JSON.stringify(next)

    createThread(text, draft, author)
  }

  return (
    <div
      id={NEW_THREAD_LAYER_ID}
      className={className}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerLeave={resetTarget}
      onClick={onLayerClick}
    >
      <NewThreadForm position={position} onSubmit={onSubmit} />
    </div>
  )
}
